import * as React from 'react'
import { supabase } from './supabase'
import { downloadInvoice } from './invoiceApi'
import { BookingSection } from './BookingDetails'
import { Button } from './ui'
import { auditEventDisplay, type BookingHistoryData, type BookingDocument } from './bookingHistoryModel'

const recordedTime = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/London' })
const empty: BookingHistoryData = { documents: [], events: [] }
const status: Record<string, string> = { ready: 'Issued', superseded: 'Superseded', cancelled: 'Cancelled', generating: 'Unfinished', failed: 'Unfinished', linked: 'Linked' }
const errorText = (error: unknown) => error && typeof error === 'object' && 'message' in error ? String(error.message) : String(error)

export function BookingHistory({ source, bookingId, refreshKey }: { source: 'manual' | 'online'; bookingId: string; refreshKey: string }) {
  const [data, setData] = React.useState<BookingHistoryData | null>(null)
  const [error, setError] = React.useState(''), [downloadError, setDownloadError] = React.useState('')
  const [revision, setRevision] = React.useState(0), [downloading, setDownloading] = React.useState<string | null>(null)
  React.useEffect(() => {
    let active = true
    const load = async () => {
      try {
        const { data, error } = await supabase.rpc('admin_booking_history', { p_source: source, p_booking_id: bookingId })
        if (error) throw error
        if (active) { setData(data || empty); setError('') }
      } catch (error) { if (active) setError(errorText(error)) }
    }
    void load()
    return () => { active = false }
  }, [source, bookingId, refreshKey, revision])
  const download = async (document: BookingDocument) => {
    if (!document.invoice_id || downloading) return
    setDownloading(document.id); setDownloadError('')
    try { await downloadInvoice(document.invoice_id, document.title) }
    catch (error) { setDownloadError(errorText(error)) }
    finally { setDownloading(null) }
  }
  const events = (data?.events || []).flatMap(event => { const display = auditEventDisplay(event); return display ? [{ ...event, ...display }] : [] })
  return <>
    <BookingSection title="Document history">
      {error && <p role="alert">History could not be loaded: {error} <Button type="button" onClick={() => setRevision(value => value + 1)}>Retry history</Button></p>}
      {downloadError && <p role="alert">{downloadError}</p>}
      {!data && !error && <p className="booking-muted" role="status">Loading history…</p>}
      {data && (data.documents.length ? <ul className="booking-document-list">{data.documents.map(document => <li key={document.id}>
        {document.downloadable ? <button type="button" className="booking-document-link" disabled={downloading !== null} onClick={() => void download(document)} aria-label={`Download ${document.title}`}>
          {downloading === document.id ? 'Downloading…' : document.title}
        </button> : document.url && /^https?:\/\//i.test(document.url) ? <a href={document.url} target="_blank" rel="noreferrer">{document.title} ↗</a> : <span>{document.title}</span>}
        <span className="booking-muted">{document.deleted_extension ? 'Deleted extension' : document.extension ? 'Extension' : 'Booking'} · {status[document.state] || document.state} · <time dateTime={document.created_at}>{recordedTime.format(new Date(document.created_at))}</time></span>
      </li>)}</ul> : !error && <p className="booking-muted">No documents recorded.</p>)}
    </BookingSection>
    <section className="booking-section" aria-label="Audit log">
      <details className="booking-audit"><summary><h3>Audit log</h3></summary>
        {error ? <p className="booking-muted">History could not be loaded. Retry above.</p> : !data ? <p className="booking-muted">Loading history…</p> : events.length ? <ol className="booking-audit-timeline">{events.map(event => <li key={event.id}>
          <strong>{event.title}</strong>
          <span className="booking-muted"><time dateTime={event.created_at}>{recordedTime.format(new Date(event.created_at))}</time> · {event.actor}</span>
          {event.details.map((detail, index) => <p key={index}>{detail}</p>)}
        </li>)}</ol> : <p className="booking-muted">No audit entries recorded.</p>}
      </details>
    </section>
  </>
}
